import type { DemoFlow } from "./flowTypes";
import { CTA_INTENT_CONFIG } from "./sectorConfigs";

export type SignalEventType =
  | "demo_started"
  | "service_selected"
  | "campaign_viewed"
  | "gallery_viewed"
  | "image_viewed"
  | "video_engaged"
  | "question_answered"
  | "cta_reached"
  | "cta_clicked"
  | "question_cta_clicked"
  | "appointment_cta_clicked"
  | "cta_intent";

export interface SignalEventMeta {
  categoryName?: string;
  ctaLabel?: string;
  ctaId?: string;
}

export interface SignalEvent {
  id: string;
  type: SignalEventType;
  label: string;
  points: number;
  meta?: SignalEventMeta;
}

export interface DemoSignalState {
  score: number;
  events: SignalEvent[];
}

export interface SignalCopy {
  title: string;
  description: string;
}

/* Tek kart tipi: normal sinyal ya da potansiyel seviyesi yükselmesi. */
export interface SignalCardData {
  id: number;
  kind: "signal" | "levelup";
  title: string;
  description: string;
  points: number;
}

/* Deterministik puan konfigürasyonu — tek kaynak. Random YOK. */
export const SIGNAL_POINTS: Record<SignalEventType, number> = {
  demo_started: 1,
  service_selected: 2,
  campaign_viewed: 1,
  gallery_viewed: 1,
  image_viewed: 1,
  video_engaged: 2,
  question_answered: 1,
  cta_reached: 3,
  cta_clicked: 5,
  question_cta_clicked: 3,
  appointment_cta_clicked: 5,
  cta_intent: 0,
};

export const SIGNAL_EVENT_LABEL: Record<SignalEventType, string> = {
  demo_started: "Demo başlatıldı",
  service_selected: "Hizmet seçimi algılandı",
  campaign_viewed: "Kampanyalar incelendi",
  gallery_viewed: "Galeri incelendi",
  image_viewed: "Görsel incelendi",
  video_engaged: "Video izlendi",
  question_answered: "Cevap alındı",
  cta_reached: "İletişim adımına ulaşıldı",
  cta_clicked: "İletişim bağlantısına tıklandı",
  question_cta_clicked: "Soru CTA'sına tıklandı",
  appointment_cta_clicked: "Randevu CTA'sına tıklandı",
  cta_intent: "Aksiyon seçildi",
};

/* Her event için 3 parçalı deterministik kopya: başlık + açıklama + puan. AI YOK.
   gallery_viewed başlığı categoryName ile override edilir (emit'te). */
export const SIGNAL_COPY: Record<SignalEventType, SignalCopy> = {
  demo_started: {
    title: "Demo başlatıldı",
    description: "Örnek müşteri yolculuğu başladı.",
  },
  service_selected: {
    title: "Hizmet ilgisi algılandı",
    description: "Seçilen hizmet kaydedildi ve müşterinin ilgi alanı netleşti.",
  },
  campaign_viewed: {
    title: "Kampanyalar incelendi",
    description: "Güncel tekliflere ulaşıldı.",
  },
  gallery_viewed: {
    title: "Galeri incelendi",
    description: "Bu hizmet kategorisine ait çalışmalar görüntülendi.",
  },
  image_viewed: {
    title: "Görsel incelendi",
    description: "İçerik etkileşimi güçleniyor.",
  },
  video_engaged: {
    title: "Video izlendi",
    description: "Yüksek içerik etkileşimi algılandı.",
  },
  question_answered: {
    title: "Bilgi paylaşımı yapıldı",
    description: "Müşteri bilgisi toplama adımı ilerledi.",
  },
  cta_reached: {
    title: "İletişim adımına ulaşıldı",
    description: "Dönüşüm niyeti güçleniyor.",
  },
  cta_clicked: {
    title: "Yüksek dönüşüm niyeti",
    description: "Müşteri doğrudan iletişim kanalına yönelmeyi seçti.",
  },
  question_cta_clicked: {
    title: "İletişim niyeti algılandı",
    description: "Müşteri işlemler hakkında doğrudan bilgi almak istedi.",
  },
  appointment_cta_clicked: {
    title: "Yüksek randevu niyeti",
    description: "Müşteri doğrudan randevu oluşturma adımını seçti.",
  },
  cta_intent: {
    title: "Aksiyon seçildi",
    description: "Müşteri bir iletişim aksiyonu seçti.",
  },
};

export interface IntentLevel {
  min: number;
  max: number;
  label: string;
  narrative: string;
}

export const INTENT_LEVELS: IntentLevel[] = [
  { min: 0, max: 3, label: "Düşük Etkileşim", narrative: "düşük etkileşimli bir ziyaretçi" },
  { min: 4, max: 7, label: "İlgili Ziyaretçi", narrative: "ilgili bir ziyaretçi" },
  { min: 8, max: 11, label: "Güçlü Potansiyel", narrative: "güçlü bir potansiyel" },
  {
    min: 12,
    max: Number.POSITIVE_INFINITY,
    label: "Yüksek Niyetli Potansiyel",
    narrative: "yüksek niyetli bir potansiyel",
  },
];

export function intentLevelFor(score: number): string {
  for (const l of INTENT_LEVELS) {
    if (score >= l.min && score <= l.max) return l.label;
  }
  return "Düşük Etkileşim";
}

/* Anlatı diliyle sınıflandırma cümlesi için küçük harf formu. */
export function intentNarrativeFor(score: number): string {
  for (const l of INTENT_LEVELS) {
    if (score >= l.min && score <= l.max) return l.narrative;
  }
  return "düşük etkileşimli bir ziyaretçi";
}

/* Akıştaki gerçek adımlardan ulaşılabilecek maksimum skoru hesaplar. */
export function computeMaxScore(flow: DemoFlow): number {
  let max = SIGNAL_POINTS.demo_started;
  for (const step of flow.steps) {
    if (step.type === "choice") max += SIGNAL_POINTS.service_selected;
    else if (step.type === "gallery") max += SIGNAL_POINTS.gallery_viewed;
    else if (step.type === "campaign_list") max += SIGNAL_POINTS.campaign_viewed;
    else if (step.type === "text" && step.id === "campaign") max += SIGNAL_POINTS.campaign_viewed;
    else if (step.type === "image") max += SIGNAL_POINTS.image_viewed;
    else if (step.type === "video") max += SIGNAL_POINTS.video_engaged;
    else if (step.type === "question") max += SIGNAL_POINTS.question_answered;
    else if (step.type === "cta") max += SIGNAL_POINTS.cta_reached + SIGNAL_POINTS.cta_clicked;
    else if (step.type === "action_set") {
      const maxIntentScore = step.actions.reduce(
        (m, a) => Math.max(m, CTA_INTENT_CONFIG[a.intent]?.score ?? 0),
        0,
      );
      max += SIGNAL_POINTS.cta_reached + maxIntentScore;
    } else if (step.type === "dual_cta")
      max += SIGNAL_POINTS.cta_reached + SIGNAL_POINTS.appointment_cta_clicked;
  }
  return max;
}

/* Label'daki emoji/sembol önekini kaldırır (yalnızca presentation temizliği;
   intent belirleme DEĞİL). */
function stripEmojiPrefix(label: string): string {
  return label.replace(/^[^A-Za-zÇĞİÖŞÜçğıöşü0-9]+/, "").trim();
}

/* Gerçekleşen event'lerden okunabilir özet maddeleri üretir. */
export function buildSignalSummary(events: SignalEvent[]): string[] {
  const count: Record<string, number> = {};
  for (const e of events) count[e.type] = (count[e.type] || 0) + 1;

  const bullets: string[] = [];
  if (count.service_selected) bullets.push("Hizmet seçti");
  if (count.gallery_viewed) {
    bullets.push(
      count.gallery_viewed === 1 ? "1 galeri inceledi" : `${count.gallery_viewed} galeri inceledi`,
    );
  }
  if (count.campaign_viewed) bullets.push("Kampanyaları inceledi");
  if (count.image_viewed) {
    bullets.push(
      count.image_viewed === 1 ? "1 görsel inceledi" : `${count.image_viewed} görsel inceledi`,
    );
  }
  if (count.video_engaged) bullets.push("Videoyu izledi");
  if (count.question_answered) {
    bullets.push(
      count.question_answered === 1
        ? "1 soruya cevap verdi"
        : `${count.question_answered} soruya cevap verdi`,
    );
  }
  if (count.cta_reached) bullets.push("İletişim adımına ulaştı");
  const ctaIntent = events.find((e) => e.type === "cta_intent");
  if (ctaIntent?.meta?.ctaLabel)
    bullets.push(`${stripEmojiPrefix(ctaIntent.meta.ctaLabel)} seçeneğini seçti`);
  else if (count.appointment_cta_clicked) bullets.push("Randevu oluşturma seçeneğini seçti");
  else if (count.question_cta_clicked) bullets.push("Soru sorma seçeneğini seçti");
  else if (count.cta_clicked) bullets.push("İletişim bağlantısını kullandı");
  return bullets;
}

/* Müşteri dilinde deterministik davranış anlatısı üretir. */
export function buildBehaviorStory(events: SignalEvent[]): string[] {
  const count: Record<string, number> = {};
  const galleryNames: string[] = [];
  for (const e of events) {
    count[e.type] = (count[e.type] || 0) + 1;
    if (e.type === "gallery_viewed" && e.meta?.categoryName) {
      galleryNames.push(e.meta.categoryName);
    }
  }

  const isBeauty = galleryNames.length > 0;

  const story: string[] = [];
  if (count.service_selected) story.push("hizmet seçti");
  for (const name of galleryNames) story.push(`${name} galerisini inceledi`);
  if (count.image_viewed) {
    story.push(
      count.image_viewed === 1
        ? "1 görsel görüntüledi"
        : `${count.image_viewed} görsel görüntüledi`,
    );
  }
  if (count.video_engaged) {
    story.push(
      isBeauty
        ? count.video_engaged === 1
          ? "1 işlem videosunu izledi"
          : `${count.video_engaged} işlem videosunu izledi`
        : "videonuzu izledi",
    );
  }
  if (count.campaign_viewed) story.push("güncel kampanyalara ulaştı");
  if (count.question_answered) {
    story.push(
      count.question_answered === 1
        ? "1 bilgi sorusuna cevap verdi"
        : `${count.question_answered} bilgi sorusuna cevap verdi`,
    );
  }
  const ctaIntent = events.find((e) => e.type === "cta_intent");
  if (ctaIntent?.meta?.ctaLabel)
    story.push(`${stripEmojiPrefix(ctaIntent.meta.ctaLabel)} seçeneğini seçti`);
  else if (count.appointment_cta_clicked) story.push("randevu oluşturma seçeneğini seçti");
  else if (count.question_cta_clicked) story.push("soru sorma seçeneğini seçti");
  else if (count.cta_clicked) story.push("iletişim bağlantısını kullandı");
  else if (count.cta_reached) story.push("iletişim aşamasına kadar ilerledi");
  return story;
}